import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CodeWordPicker } from "@/components/CodeWordPicker";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { CAN_ADD, PLACE_WORDS, ADD_WORDS } from "@/lib/placeWords";
import { MadeBox, PlanCodes, SlotSizePick, Stepper, WhatChips } from "./addHereParts";

const FIXED = { zone: "zone", spot: "spot", shelf_unit: "structure" };
const wordOf = (label) => (label || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

// ONE WAY TO ADD ANYTHING (his design, 21 Oct): tap + Add here, pick what it is, say how many, see every code, Make.
export function AddHereSheet({ node, kind, where, templates, types, onClose, onChanged, onShelves, onPrint, onPrintSheet }) {
  const kinds = useMemo(() => CAN_ADD[kind] || [], [kind]);
  const tpls = useMemo(() => templates.filter((t) => kinds.includes(t.what)), [templates, kinds]);
  const [what, setWhat] = useState(null);
  const [tplId, setTplId] = useState(null);
  const [f, setF] = useState({ name: "", count: 1, slots: 0, slot_word: "Slot", bays: 8, levels: 6, floor: true, container_type: "", code_word: "" });
  const [pick, setPick] = useState(null);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [saveAs, setSaveAs] = useState("");
  const [saveOn, setSaveOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const [undo, setUndo] = useState({ busy: false, done: null });
  const [slotSize, setSlotSize] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const tpl = tpls.find((t) => t.id === tplId);
  const hasSlots = what === "slots" || (what === "holder" && (tpl ? tpl.slots : f.slots) > 0);
  const sizeNow = slotSize || tpl?.slot_label_size || "4x3";
  // a new rack or one new holder prints through the one Print labels sheet (preview first), so no count is promised here
  const sheetPrint = (tpl?.what || what) === "pallet_rack" || ((tpl?.what || what) === "holder" && (f.count || 1) === 1);

  const choose = (w, id) => {
    if (w === "shelves") { onShelves(node); onClose(); return; }
    setWhat(w); setTplId(id); setPlan(null); setErr(null); setPick(null); setSaveOn(false); setSaveAs(""); setSlotSize(null);
  };
  const body = useMemo(() => ({
    parent_id: node.id, what, template_id: tplId || undefined, name: f.name, count: f.count,
    code_word: FIXED[what] ? pick?.word : (f.code_word || undefined), slots: what === "slots" ? f.count : f.slots,
    slot_word: f.slot_word, bays: f.bays, levels: f.levels, floor: f.floor, container_type: f.container_type || undefined,
    slot_label_size: slotSize || undefined,
  }), [node.id, what, tplId, f, pick, slotSize]);
  const ready = what && (tplId || !FIXED[what] || (f.name.trim() && pick)) && (what !== "holder" || tplId || f.code_word);

  useEffect(() => {
    if (!ready || made) { setPlan(null); return undefined; }
    const t = setTimeout(() => api.post("/places/plan", body)
      .then(({ data }) => { setPlan(data); setErr(null); })
      .catch((e) => { setPlan(null); setErr(formatApiErrorDetail(e.response?.data?.detail)); }), 250);
    return () => clearTimeout(t);
  }, [body, ready, made]);

  const make = async (andPrint) => {
    setBusy(true);
    try {
      const { data } = await api.post("/places/add", { ...body, save_template: saveOn && saveAs.trim() ? saveAs.trim() : undefined });
      setMade(data);
      toast.success(`Made ${data.count}.`);
      onChanged();
      if (andPrint) printMade(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // ONE PRINT LABELS FLOW for a new rack or a new holder — preview first; the scan step stays open underneath
  const one = (m) => ({ id: m.roots[0], short_code: m.made[0].short_code, name: m.made[0].name });
  const printMade = (m) => (m.what === "pallet_rack" ? onPrintSheet(one(m), "rack")
    : m.what === "holder" && m.roots.length === 1 ? onPrintSheet(one(m), "holder")
    : onPrint(m.roots, { holderSize: m.label_sizes?.holder, slotSize: m.label_sizes?.slot || "4x3", title: `${m.codes[0]}…` }));
  const runUndo = async () => {
    setUndo({ busy: true, done: null });
    try { const { data } = await api.post(`/places/add/${made.setup_id}/undo`); setUndo({ busy: false, done: data.message }); onChanged(); }
    catch (e) { setUndo({ busy: false, done: null }); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-here-sheet" className="rounded-xl sm:max-w-xl max-h-kb overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Add inside {where.split(" › ").pop() || node.name}</DialogTitle>
          <div className="text-[12px] text-slate-500" data-testid="add-here-where">{where} · {PLACE_WORDS[kind]?.word}: {PLACE_WORDS[kind]?.line}</div>
        </DialogHeader>
        {made ? (
          <MadeBox made={made} onUndo={runUndo} undoBusy={undo.busy} undone={undo.done}
            onPrint={() => printMade(made)} onRackLabels={() => printMade(made)} onMoved={onChanged} />
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">What is it?</div>
              <WhatChips templates={tpls} kinds={kinds} what={what} tplId={tplId} onPick={choose} />
            </div>
            {what && <Fields what={what} tpl={tpl} f={f} set={set} node={node} types={types} onPick={setPick} />}
            {hasSlots && <SlotSizePick value={sizeNow} onChange={setSlotSize} />}
            <PlanCodes plan={plan} error={err} />
            {plan && !tplId && ["holder", "pallet_rack"].includes(what) && (
              <label className="flex flex-wrap items-center gap-2 text-sm" data-testid="add-here-save-template">
                <input type="checkbox" data-testid="add-here-save-template-check" checked={saveOn} onChange={(e) => setSaveOn(e.target.checked)} className="w-5 h-5 accent-[#E0781A]" />
                Save as a template
                {saveOn && <Input data-testid="add-here-save-template-name" value={saveAs} onChange={(e) => setSaveAs(e.target.value)}
                  placeholder="e.g. Glass rack, 12 slots" className="h-9 w-56" />}
              </label>
            )}
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
              <Button data-testid="add-here-make" disabled={!plan || busy || (saveOn && !saveAs.trim())} onClick={() => make(false)} className="rounded-lg bg-[#1F1F1F] text-white">Make</Button>
              <Button data-testid="add-here-make-print" disabled={!plan || busy || (saveOn && !saveAs.trim())} onClick={() => make(true)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : sheetPrint ? "Make, then print labels" : `Make & print${plan ? ` ${plan.count} label${plan.count === 1 ? "" : "s"}` : ""}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Fields({ what, tpl, f, set, node, types, onPick }) {
  if (tpl) {
    return tpl.what === "holder"
      ? <Stepper label="How many" value={f.count} onChange={(v) => set("count", v)} max={50} testid="add-here-count" />
      : <p className="text-sm text-slate-600" data-testid="add-here-tpl-shape">{tpl.summary}</p>;
  }
  if (FIXED[what]) {
    return (
      <div className="space-y-2">
        <Input data-testid="add-here-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={`Name of the ${ADD_WORDS[what].word.toLowerCase()}, e.g. ${what === "zone" ? "East Apron" : "Front shelf"}`} className="h-10" />
        {f.name.trim() && <CodeWordPicker name={f.name} type={FIXED[what]} parentId={node.id} onResolved={onPick} />}
      </div>
    );
  }
  if (what === "pallet_rack") {
    return (
      <div className="space-y-2">
        <Stepper label="Bays" value={f.bays} onChange={(v) => set("bays", v)} max={60} testid="add-here-bays" />
        <Stepper label="Levels" value={f.levels} onChange={(v) => set("levels", v)} max={24} testid="add-here-levels" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="add-here-floor" checked={f.floor} onChange={(e) => set("floor", e.target.checked)} className="w-5 h-5" />The bottom level is the floor (pallets on the ground)</label>
      </div>
    );
  }
  if (what === "slots") return <Stepper label="How many slots" value={f.count} onChange={(v) => set("count", v)} max={60} testid="add-here-count" />;
  return (
    <div className="space-y-2">
      <Select value={f.container_type || "none"} onValueChange={(v) => { const t = types.find((x) => x.key === v); set("container_type", v === "none" ? "" : v); if (t && wordOf(t.label).length <= 8) set("code_word", wordOf(t.label)); }}>
        <SelectTrigger data-testid="add-here-holder-type" className="h-10"><SelectValue placeholder="What kind of holder?" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— Pick a kind —</SelectItem>
          {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input data-testid="add-here-holder-word" value={f.code_word} onChange={(e) => set("code_word", e.target.value.toUpperCase())} placeholder="Its code word, e.g. GLASS (2–8, never cut)" className="h-10 font-mono w-72" />
      <Stepper label="How many" value={f.count} onChange={(v) => set("count", v)} max={50} testid="add-here-count" />
      <Stepper label="Slots each" value={f.slots} onChange={(v) => set("slots", v)} min={0} max={60} testid="add-here-slots" />
    </div>
  );
}
